import { ArrowLeft, ChevronRight, CircleAlert, CircleCheck, Clock, Eye, EyeOff, Hourglass, Info, KeyRound, RefreshCw, ServerOff, ShieldX, UserRoundX, WifiOff, X } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { motion } from 'motion/react';
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Button, CopyButton, IconButton, Input, OrgusLockup, Skeleton, cn } from '@orgus/ui';
import { startData } from '../../lib/data.ts';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import type { AuthProviders, SignInProvider, SignInProviderId, SigninHintCommands } from '@orgus/protocol';
import { useSigninHint } from './signinHint.ts';
import { useOrgus } from '../../lib/store.ts';
import { useAutoRetry } from './autoRetry.ts';
import { BusyButton } from './BusyButton.tsx';
import { authClient, signInButtons } from './client.ts';
import { mockSignInState, mockSignInStateProps } from './mockAuth.ts';
import { providerLabel } from './labels.ts';
import { ProviderMark } from './ProviderMark.tsx';
import { SignInBrandPanel } from './SignInBrandPanel.tsx';
import { BLOCKING_KINDS, SignInStateScreen } from './SignInStateScreen.tsx';
import { COMMAND, checkLinkInput, hubStateOf, isLinkRefusal, linkErrorMessage, linkFailureNotice, serverKindOf, signInNotice } from './signinStates.ts';
import type { HubState, SignInNotice, SignInNoticeKind, SignInNoticeTone, SignedOutReason } from './signinStates.ts';
import { openedWithOwnerLink, useMe } from './useMe.ts';

const NOTICE_ICON: Record<SignInNoticeKind, LucideIcon> = {
  offline: WifiOff,
  unreachable: ServerOff,
  'access-denied': UserRoundX,
  'provider-error': CircleAlert,
  'link-rejected': Hourglass,
  'rate-limited': Clock,
  'session-ended': Clock,
  'access-ended': ShieldX,
};

const NOTICE_TONE: Record<SignInNoticeTone, { box: string; icon: string }> = {
  failed: { box: 'border-failed/30 bg-failed-soft', icon: 'text-failed' },
  offline: { box: 'border-offline/30 bg-offline-soft', icon: 'text-offline' },
  neutral: { box: 'border-border bg-surface', icon: 'text-fg-muted' },
};

/** navigator.onLine as React state. The server render (tests) assumes online. */
function subscribeOnline(cb: () => void): () => void {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
}
export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );
}

function currentHost(): string {
  return typeof window !== 'undefined' ? window.location.host : 'this hub';
}

/** How an owner sign-in link on this hub begins. The placeholder shows it, and the hint repeats it at full contrast. */
function linkPrefixFor(): string {
  return `${typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:4600'}/#token=`;
}

/** Commands in a sentence (orgus up, orgus token) set in mono, so they read as something to type. */
export function withCommands(text: string): ReactNode {
  const parts = text.split(COMMAND);
  if (parts.length === 1) return text;
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <span key={i} className="font-mono text-fg">
        {p}
      </span>
    ) : (
      p
    ),
  );
}

/**
 * A notice above sign-in methods that still work: icon plus status label, then what happened,
 * why, and what to do next. The methods under it are the retry, so it carries no button of its own.
 */
export function SignInNoticeView({
  notice,
  onDismiss,
  focusOnMount = false,
  id,
}: {
  id?: string;
  notice: SignInNotice;
  onDismiss?: () => void;
  focusOnMount?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [why, setWhy] = useState(false);
  // An error that is already there when the page opens is not announced by every screen
  // reader, so it takes focus once: the first thing read is what went wrong.
  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);
  const Icon = NOTICE_ICON[notice.kind];
  const tone = NOTICE_TONE[notice.tone];
  return (
    <div
      ref={ref}
      id={id}
      tabIndex={-1}
      role={notice.urgent ? 'alert' : 'status'}
      data-notice={notice.kind}
      className={cn('flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left outline-none', tone.box)}
    >
      <Icon className={cn('mt-0.5 size-4 shrink-0', tone.icon)} aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className={cn('text-sm font-medium', tone.icon)}>{notice.label}</p>
        <p className="text-base font-medium text-fg">{withCommands(notice.what)}</p>
        {/* One line at rest: what to do next. The reason is one click away, as on the full-screen states. */}
        {(notice.next || notice.why) && <p className="text-sm text-fg-muted">{withCommands(notice.next ?? notice.why!)}</p>}
        {notice.next && notice.why && (
          <>
            <Button
              variant="ghost"
              size="sm"
              icon={<ChevronRight className={cn('transition-transform duration-(--duration-fast)', why && 'rotate-90')} />}
              aria-expanded={why}
              onClick={() => setWhy((v) => !v)}
              className="-ml-2 self-start text-fg-subtle"
            >
              Why this happened
            </Button>
            {why && <p className="text-sm text-fg-muted">{withCommands(notice.why)}</p>}
          </>
        )}
      </div>
      {onDismiss && <IconButton label="Dismiss" icon={<X />} size="xs" onClick={onDismiss} className="-mr-1 -mt-0.5" />}
    </div>
  );
}

/** The server's state at the foot of the form: icon plus label, then the address. */
function HubStatus({ host, state, hosted }: { host: string; state: HubState; hosted: boolean }) {
  const who = hosted ? 'Orgus' : 'Hub';
  const s =
    state === 'ok'
      ? { Icon: CircleCheck, label: `${who} reachable`, cls: 'text-passed' }
      : state === 'offline'
        ? { Icon: WifiOff, label: 'Offline', cls: 'text-offline' }
        : { Icon: ServerOff, label: `${who} unreachable`, cls: 'text-failed' };
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-sm text-fg-subtle" data-hub-status={state}>
      <s.Icon className={cn('size-3.5 shrink-0', s.cls)} aria-hidden />
      <span className="text-fg-muted">{s.label}</span>
      <span aria-hidden className="text-fg-faint">
        ·
      </span>
      <span className="truncate font-mono">{host}</span>
    </p>
  );
}

export interface SignInScreenProps {
  /** Why the screen is up, when AuthGate saw it happen (session ended, signed out). */
  reason?: SignedOutReason | null;
  /** The hub could not be asked who we are while this screen was up. */
  hubError?: UiError | null;
  /** Ask the hub again (Try again on the unreachable notice). */
  onRetry?: () => Promise<void> | void;
}

/**
 * The sign-in screen. Wide screens: the form beside the brand panel; narrow screens: the form.
 * It offers only what the hub reports (GET /api/auth/providers): one button per sign-in
 * provider that is on (GitHub, Google, Microsoft, in that order), and the owner sign-in link
 * the hub prints, which always works. A hub that cannot list them gets the owner link alone.
 */
export function SignInScreen(props: SignInScreenProps) {
  const providers = useMe((s) => s.providers);
  const status = useMe((s) => s.status);
  const signinError = useMe((s) => s.signinError);
  const clearSigninError = useMe((s) => s.clearSigninError);
  const mode = useOrgus((s) => s.mode);
  const phase = useOrgus((s) => s.phase);
  const loadError = useOrgus((s) => s.loadError);
  const online = useOnline();
  // Demo data only: &signin-state= opens the screen in one of its states, until it is dismissed.
  const [demo, setDemo] = useState(() => (mode === 'mock' ? mockSignInStateProps(mockSignInState()) : {}));
  // The control plane signs in through GitHub and has no orgus token: it is never asked.
  const tokenHint = useSigninHint(providers == null || serverKindOf(providers.server ?? 'hub') === 'control-plane', mode === 'mock');
  return (
    <SignInView
      {...props}
      providers={providers}
      checking={status === 'loading'}
      signinError={signinError}
      onClearSigninError={clearSigninError}
      mock={mode === 'mock'}
      openedWithLink={openedWithOwnerLink()}
      bootError={phase === 'error' ? loadError : null}
      online={online}
      host={currentHost()}
      tokenHint={tokenHint}
      {...demo}
      {...(demo.signinError ? { onClearSigninError: () => setDemo({}) } : {})}
    />
  );
}

export interface SignInViewProps extends SignInScreenProps {
  providers: AuthProviders | null;
  /** Still asking the hub which methods it has. */
  checking: boolean;
  /** `link`: an owner link from the address bar that did not work; it comes back in the field. */
  signinError: { code: string; login: string | null; provider?: SignInProviderId | null; link?: string } | null;
  onClearSigninError: () => void;
  mock: boolean;
  /** The page was opened with #token=... in the address bar. */
  openedWithLink: boolean;
  /** The data layer's first-load error (the owner link exchange runs there). */
  bootError: UiError | null;
  online: boolean;
  host: string;
  /** The orgus token command for this hub's own home (QA-79), or null for today's words. */
  tokenHint?: SigninHintCommands | null;
}

/** One command to run, with a copy button: set in mono so it reads as something to type. */
function CommandRow({ shell, command }: { shell: string; command: string }) {
  return (
    <div className="flex min-w-0 items-start gap-2 rounded-control border border-border bg-surface px-2.5 py-1.5" data-command={shell}>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-caption text-fg-subtle">{shell}</span>
        <code className="break-all font-mono text-sm text-fg">{command}</code>
      </div>
      <CopyButton text={command} label={`Copy the ${shell} command`} className="-mr-1 shrink-0" />
    </div>
  );
}

/** QA-79: the exact command for a fresh owner link on this hub, in PowerShell and bash. */
function TokenCommands({ hint }: { hint: SigninHintCommands }) {
  return (
    <div className="flex flex-col gap-1.5" data-token-hint>
      <p className="text-sm text-fg-muted">For a fresh link, run one of these on the hub computer.</p>
      <CommandRow shell="PowerShell" command={hint.powershell} />
      <CommandRow shell="bash" command={hint.bash} />
    </div>
  );
}

/** The sign-in screen from its inputs, so every state renders without stores (tests, previews). */
export function SignInView({
  reason = null,
  hubError = null,
  onRetry,
  providers,
  checking,
  signinError,
  onClearSigninError: clearSigninError,
  mock,
  openedWithLink,
  bootError,
  online,
  host,
  tokenHint = null,
}: SignInViewProps) {
  const buttons = signInButtons(providers);
  const anyProvider = buttons.length > 0;
  // The hosted control plane says so; until it answers, what this browser saw last time here.
  const hosted = serverKindOf(providers ? (providers.server ?? 'hub') : null) === 'control-plane';
  // Self-serve signup (ADR 0080): a new account makes its own workspace, so say so up front.
  const signup = hosted && !!providers?.signup;
  const [linkOpen, setLinkOpen] = useState(false);
  // A link from the address bar that did not work comes back here, so it can be fixed or replaced.
  const [link, setLink] = useState(() => signinError?.link ?? '');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<UiError | null>(null);
  const [busy, setBusy] = useState<SignInProviderId | 'link' | null>(null);
  const [showLink, setShowLink] = useState(false);
  const [dismissedBoot, setDismissedBoot] = useState(false);
  const [dismissedReason, setDismissedReason] = useState(false);
  const linkInput = useRef<HTMLInputElement>(null);
  const linkErrorId = useId();
  const noticeId = useId();
  const linkPrefix = linkPrefixFor();
  const [mountedWith] = useState(() => !!(signinError || (openedWithLink && bootError)));

  // Back from the provider with the browser's back button (bfcache): the redirect is over.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) setBusy(null);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  // Only the hub's refusal of the link in the address bar, and only before anyone signed in
  // on this page: a later 401 (a sign-out, an ended session) is not about that link.
  const linkBootError = openedWithLink && reason == null && !dismissedBoot && isLinkRefusal(bootError) ? bootError : null;
  const notice = signInNotice({
    host,
    online,
    hubError,
    signinError,
    linkBootError,
    reason: dismissedReason ? null : reason,
    hosted,
  });
  const dismiss =
    notice == null || notice.kind === 'offline' || notice.kind === 'unreachable'
      ? undefined
      : notice.kind === 'access-denied' || notice.kind === 'provider-error'
        ? clearSigninError
        : notice.kind === 'link-rejected' || notice.kind === 'rate-limited'
          ? () => setDismissedBoot(true)
          : () => setDismissedReason(true);
  const hubState = hubStateOf(online, hubError);
  const hubDown = hubState !== 'ok';
  // Offline, the hub not answering, and an account the hub refused stop sign-in altogether: they
  // get the form side to themselves. Every other notice sits above the methods that still work.
  const screen = notice && BLOCKING_KINDS.has(notice.kind) && (notice.kind !== 'unreachable' || hubState === 'unreachable') ? notice : null;
  const showLinkForm = !anyProvider || linkOpen || linkError != null || linkBootError != null || !!signinError?.link;
  // The hub does not answer: asking it again is the one thing that can work, so it leads, and
  // it happens by itself on a countdown. Offline waits for the browser's online event instead.
  const autoRetry = useAutoRetry(hubState === 'unreachable', () => (onRetry ? onRetry() : useMe.getState().load()));

  // Leave for the provider, and come back to this page once it confirms who you are. The
  // button stays busy until the page unloads (or comes back from the bfcache, above).
  const signInWith = (p: SignInProvider) => {
    if (busy || hubDown) return;
    setBusy(p.id);
    clearSigninError();
    const next = `${window.location.pathname}${window.location.search}`;
    void authClient.startSignIn(p, next).then(
      () => (mock ? useMe.getState().load().then(() => setBusy(null)) : undefined),
      () => setBusy(null),
    );
  };
  // Try again on a sign-in error: the same provider, when it is still on.
  const retryProvider = notice?.provider ? buttons.find((b) => b.id === notice.provider) : undefined;
  const busyProvider = buttons.find((b) => b.id === busy);

  const signInWithLink = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || hubDown) return;
    const invalid = checkLinkInput(link);
    setFieldError(invalid);
    setLinkError(null);
    if (invalid) return;
    // A new attempt replaces the notice about the link the page opened with: one answer at a time.
    clearSigninError();
    setDismissedBoot(true);
    setBusy('link');
    try {
      await authClient.signInWithOwnerLink(link);
      setLink('');
      setDismissedBoot(true);
      if (!mock) startData();
      await useMe.getState().load();
    } catch (err) {
      setLinkError(linkErrorMessage(toUiError(err), host, hosted));
      // The button was disabled while it worked, which drops focus: put it back in the field,
      // selected, so pasting a newer link replaces the old one.
      setTimeout(() => {
        linkInput.current?.focus();
        linkInput.current?.select();
      }, 0);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid h-full min-h-full w-full overflow-y-auto bg-bg text-fg lg:grid-cols-[minmax(0,1fr)_minmax(0,1.08fr)]">
      {/* One column, one left edge: the lockup, the form and the hub status all start on it. */}
      <main className="flex min-h-full flex-col items-center px-6 py-6 sm:px-10 lg:px-12 xl:px-16">
        <div className="flex w-full max-w-[380px] flex-1 flex-col">
          <header className="flex h-8 items-center">
            <OrgusLockup layout="compact" tagline capHeight={13} title="Orgus" />
          </header>

          {/* Anchored to the top, not centred, so the heading stays put when a notice appears. */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-1 flex-col gap-7 pb-12 pt-[clamp(--spacing(10),8vh,--spacing(20))] lg:pt-[clamp(--spacing(16),18vh,--spacing(50))]"
          >
            {screen ? (
              <SignInStateScreen key={screen.kind} id={noticeId} notice={screen} icon={NOTICE_ICON[screen.kind]} words={withCommands} focusOnMount={mountedWith && screen.urgent}>
                {screen.kind === 'unreachable' && (
                  <div className="flex flex-col gap-2">
                    <BusyButton
                      variant="primary"
                      size="lg"
                      icon={<RefreshCw />}
                      busy={autoRetry.retrying}
                      busyLabel="Trying again"
                      onClick={autoRetry.retryNow}
                      className="w-full justify-center"
                    >
                      Try again
                    </BusyButton>
                    <p className="text-sm text-fg-subtle" data-auto-retry={autoRetry.secondsLeft ?? 'now'}>
                      {autoRetry.secondsLeft == null ? `Asking ${hosted ? 'Orgus' : 'the hub'} at ${host} now.` : `Trying again by itself in ${autoRetry.secondsLeft} s.`}
                    </p>
                  </div>
                )}
                {screen.kind === 'access-denied' && (screen.provider || screen.account) && (
                  // The account that was turned away, so a person with several knows which one.
                  <div className="flex items-center gap-3 rounded-card border border-border bg-surface px-3 py-3" data-refused-account>
                    <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-control border border-border bg-bg">
                      <ProviderMark id={screen.provider ?? ''} className="size-4" />
                    </span>
                    <div className="flex min-w-0 flex-col">
                      <p className="truncate text-sm font-medium text-fg">{screen.account ?? `${providerLabel(screen.provider)} account`}</p>
                      <p className="truncate text-sm text-fg-subtle">{screen.account && screen.provider ? `${providerLabel(screen.provider)} account` : 'The account you just signed in with'}</p>
                    </div>
                  </div>
                )}
                {screen.kind === 'access-denied' && (
                  <div className="flex flex-col gap-2">
                    {retryProvider && (
                      <BusyButton
                        variant="primary"
                        size="lg"
                        icon={<ProviderMark id={retryProvider.id} />}
                        busy={busy === retryProvider.id}
                        busyLabel={`Opening ${retryProvider.label}`}
                        disabled={busy != null && busy !== retryProvider.id}
                        onClick={() => signInWith(retryProvider)}
                        className="w-full justify-center"
                      >
                        {screen.retryLabel ?? `Try again with ${retryProvider.label}`}
                      </BusyButton>
                    )}
                    <Button
                      variant={retryProvider ? 'secondary' : 'primary'}
                      size="lg"
                      icon={<ArrowLeft />}
                      disabled={busy != null}
                      onClick={clearSigninError}
                      className="w-full justify-center"
                    >
                      Use another sign-in method
                    </Button>
                  </div>
                )}
              </SignInStateScreen>
            ) : (
              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-semibold tracking-[-0.01em] text-fg">{signup ? 'Sign in or sign up' : 'Sign in to Orgus'}</h1>
                <p className="text-md text-fg-muted">{signup ? 'Use an account you already have. Your first sign-in creates your own workspace.' : `Use an account your admin has let into this ${hosted ? 'workspace' : 'hub'}.`}</p>
              </div>
            )}

            {notice && !screen && (
              <SignInNoticeView
                key={notice.kind}
                id={noticeId}
                notice={notice}
                onDismiss={dismiss}
                focusOnMount={mountedWith && notice.urgent}
              />
            )}

            {screen ? null : checking ? (
              // The shape of what is coming: two sign-in buttons at their real height.
              <div role="status" aria-label={`Checking how this ${hosted ? 'workspace' : 'hub'} signs people in`} className="flex flex-col gap-2">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {anyProvider && (
                  <div className="flex flex-col gap-2">
                    {/* One primary per surface: the first provider leads, the others are secondary. */}
                    <div className="flex flex-col gap-2" data-providers={buttons.map((b) => b.id).join(' ')}>
                      {buttons.map((p, i) => (
                        <BusyButton
                          key={p.id}
                          variant={i === 0 ? 'primary' : 'secondary'}
                          size="lg"
                          icon={<ProviderMark id={p.id} />}
                          busy={busy === p.id}
                          busyLabel={`Opening ${p.label}`}
                          disabled={busy != null && busy !== p.id}
                          data-provider={p.id}
                          data-start={p.startUrl}
                          onClick={() => signInWith(p)}
                          className="w-full justify-center"
                        >
                          Sign in with {p.label}
                        </BusyButton>
                      ))}
                    </div>
                    {/* Empty until a provider opens, so the words are announced when they appear. */}
                    <p className="text-sm text-fg-subtle" role="status">
                      {busyProvider ? `You come back here once ${busyProvider.label} confirms who you are.` : ''}
                    </p>
                  </div>
                )}

                {anyProvider && (
                  <div className="flex items-center gap-3 text-sm text-fg-subtle" aria-hidden>
                    <span className="h-px flex-1 bg-border" />
                    or
                    <span className="h-px flex-1 bg-border" />
                  </div>
                )}

                {showLinkForm ? (
                  <form className="flex flex-col gap-3" onSubmit={signInWithLink} noValidate aria-label="Sign in with the owner link">
                    <Input
                      label="Owner sign-in link"
                      hint={
                        hosted ? (
                          'The one-time link from whoever set up this workspace.'
                        ) : tokenHint ? (
                          'Printed at hub startup.'
                        ) : (
                          <>
                            Printed at hub startup, or run <span className="font-mono text-fg-muted">orgus token</span> on the hub machine.
                          </>
                        )
                      }
                      error={fieldError ?? undefined}
                      placeholder={`${linkPrefix}...`}
                      value={link}
                      onChange={(e) => {
                        setLink(e.target.value);
                        if (fieldError) setFieldError(null);
                      }}
                      ref={linkInput}
                      readOnly={busy === 'link'}
                      {...(linkError ? { 'aria-invalid': true, 'aria-describedby': linkErrorId } : {})}
                      icon={<KeyRound />}
                      // The link carries the owner's token: hidden unless asked for, like a password,
                      // so it stays off screen shares, recordings and anyone looking over a shoulder.
                      type={showLink ? 'text' : 'password'}
                      trailing={
                        <IconButton
                          label={showLink ? 'Hide the link' : 'Show the link'}
                          icon={showLink ? <EyeOff /> : <Eye />}
                          size="xs"
                          pressed={showLink}
                          onClick={() => setShowLink((v) => !v)}
                        />
                      }
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      autoFocus={anyProvider && linkOpen}
                      data-1p-ignore
                      data-lpignore="true"
                      className="font-mono"
                    />
                    {!hosted && tokenHint && <TokenCommands hint={tokenHint} />}
                    {linkError && <SignInNoticeView id={linkErrorId} notice={linkFailureNotice(linkError)} />}
                    <BusyButton
                      type="submit"
                      variant={anyProvider ? 'secondary' : 'primary'}
                      size="lg"
                      busy={busy === 'link'}
                      busyLabel="Signing in"
                      disabled={busy != null && busy !== 'link'}
                      className="w-full justify-center"
                    >
                      Sign in with the link
                    </BusyButton>
                  </form>
                ) : (
                  <Button
                    variant="secondary"
                    size="lg"
                    icon={<KeyRound />}
                    onClick={() => setLinkOpen(true)}
                    disabled={busy != null}
                    className="w-full justify-center"
                  >
                    Use the owner sign-in link
                  </Button>
                )}

                {!anyProvider && providers && (
                  <p className="flex items-start gap-2 text-sm text-fg-subtle">
                    <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                    <span>{hosted ? 'No sign-in provider is on yet: the owner sets them up with the deploy.' : 'No sign-in provider is on yet: an owner turns one on in Settings, Sign-in.'}</span>
                  </p>
                )}
              </div>
            )}
          </motion.div>

          <footer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <HubStatus host={host} state={hubState} hosted={hosted} />
          </footer>
        </div>
      </main>
      <SignInBrandPanel />
    </div>
  );
}
